package com.schoolconnect.app.feature.quickannouncements

import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.request.QuickAnnouncementCreateRequest
import com.schoolconnect.app.datasource.network.request.QuickAnnouncementUpdateRequest
import com.schoolconnect.app.datasource.network.response.QuickAnnouncementResponse
import com.schoolconnect.app.designsystem.appDateInputComparable
import com.schoolconnect.app.designsystem.currentAppDateLabel
import com.schoolconnect.app.designsystem.AppToastCenter
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.pageWindow
import com.schoolconnect.app.repository.quickannouncement.QuickAnnouncementRepository
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*

class QuickAnnouncementsViewModel {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val schoolId = UserSessionStore.getHomeRoleSchoolId().trim()
    private var loadJob: Job? = null
    private var saveJob: Job? = null
    private var mutationJob: Job? = null
    private val _uiState = MutableStateFlow(QuickAnnouncementsUiState())
    val uiState: StateFlow<QuickAnnouncementsUiState> = _uiState.asStateFlow()

    init { loadPage() }

    fun onEvent(event: QuickAnnouncementsUiEvent) {
        when (event) {
            is QuickAnnouncementsUiEvent.SearchChanged -> { _uiState.update { it.copy(searchQuery = event.value, pageIndex = 0) }; loadPage(true) }
            is QuickAnnouncementsUiEvent.StatusChanged -> { _uiState.update { it.copy(status = event.value, pageIndex = 0) }; loadPage() }
            is QuickAnnouncementsUiEvent.CreatedByChanged -> { _uiState.update { it.copy(createdBy = event.value, createdById = event.id, pageIndex = 0) }; loadPage() }
            is QuickAnnouncementsUiEvent.PeriodChanged -> { _uiState.update { it.copy(period = event.value, pageIndex = 0) }; loadPage() }
            QuickAnnouncementsUiEvent.FiltersReset -> { _uiState.value = QuickAnnouncementsUiState(); loadPage() }
            is QuickAnnouncementsUiEvent.PageChanged -> { _uiState.update { it.copy(pageIndex = event.value.coerceAtLeast(0)) }; loadPage() }
            is QuickAnnouncementsUiEvent.PageSizeChanged -> { _uiState.update { it.copy(pageSize = event.value.coerceIn(PaginationPolicy.PAGE_SIZE_OPTIONS.min(), PaginationPolicy.PAGE_SIZE_OPTIONS.max()), pageIndex = 0) }; loadPage() }
            is QuickAnnouncementsUiEvent.SortChanged -> { _uiState.update { current -> current.copy(sortBy = event.field, sortAscending = if (current.sortBy == event.field) !current.sortAscending else true, pageIndex = 0) }; loadPage() }
            is QuickAnnouncementsUiEvent.CreateRequested -> saveCreate(event.draft)
            is QuickAnnouncementsUiEvent.UpdateRequested -> saveUpdate(event.id, event.currentStatus, event.draft)
            is QuickAnnouncementsUiEvent.DuplicateRequested -> duplicate(event.id)
            is QuickAnnouncementsUiEvent.EndNowRequested -> endNow(event.id)
            is QuickAnnouncementsUiEvent.DeleteRequested -> delete(event.id)
        }
    }

    private fun loadPage(debounce: Boolean = false) {
        loadJob?.cancel()
        loadJob = scope.launch {
            if (debounce) delay(250)
            val state = _uiState.value
            _uiState.update { it.copy(isLoading = true, errorMessage = "") }
            QuickAnnouncementRepository.loadPage(state.searchQuery, state.status.toApiStatus(), state.createdById, state.period.toApiPeriod(), state.pageIndex, state.pageSize, state.sortBy, state.sortAscending, scope).fold(
                onSuccess = { response ->
                    val items = response.items.orEmpty()
                    val counters = response.counters.orEmpty()
                    val totalCount = response.count ?: items.size
                    val clampedPageIndex = pageWindow(state.pageIndex, state.pageSize, totalCount).pageIndex
                    if (clampedPageIndex != state.pageIndex) {
                        _uiState.update { it.copy(pageIndex = clampedPageIndex) }
                        loadPage()
                        return@fold
                    }
                    _uiState.update { it.copy(isLoading = false, rows = items.map(QuickAnnouncementResponse::toUi), totalCount = totalCount, totalAnnouncements = counters.counter("total") ?: totalCount, activeCount = counters.counter("active") ?: 0, expiredCount = counters.counter("expired") ?: 0, draftCount = counters.counter("draft") ?: 0) }
                },
                onFailure = { throwable -> _uiState.update { it.copy(isLoading = false, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load quick announcements." }) } }
            )
        }
    }

    private fun saveCreate(draft: QuickAnnouncementDraft) {
        val dateTime = draft.toApiDateTime()
        when {
            schoolId.isBlank() -> _uiState.update { it.copy(saveErrorMessage = "A school context is required.") }
            draft.message.trim().isBlank() -> _uiState.update { it.copy(saveErrorMessage = "Announcement is required.") }
            draft.displayUntil.isBeforeToday() -> _uiState.update { it.copy(saveErrorMessage = "Display until date cannot be earlier than today.") }
            dateTime == null -> _uiState.update { it.copy(saveErrorMessage = "Enter a valid display date and time.") }
            else -> {
                saveJob?.cancel(); saveJob = scope.launch {
                    delay(150)
                    _uiState.update { it.copy(isSaving = true, saveErrorMessage = "") }
                    QuickAnnouncementRepository.create(QuickAnnouncementCreateRequest(schoolId, draft.message.trim(), dateTime, if (draft.publish) "ACTIVE" else "DRAFT")).fold(
                        { _uiState.update { it.copy(isSaving = false, saveGeneration = it.saveGeneration + 1) }; loadPage() },
                        { error -> _uiState.update { it.copy(isSaving = false, saveErrorMessage = error.message.orEmpty().ifBlank { "Unable to save announcement." }) } }
                    )
                }
            }
        }
    }

    private fun saveUpdate(id: String, currentStatus: String, draft: QuickAnnouncementDraft) {
        val dateTime = draft.toApiDateTime()
        if (draft.message.trim().isBlank()) { _uiState.update { it.copy(saveErrorMessage = "Enter a valid announcement and display date/time.") }; return }
        if (draft.displayUntil.isBeforeToday()) { _uiState.update { it.copy(saveErrorMessage = "Display until date cannot be earlier than today.") }; return }
        if (dateTime == null) { _uiState.update { it.copy(saveErrorMessage = "Enter a valid announcement and display date/time.") }; return }
        saveJob?.cancel(); saveJob = scope.launch {
            delay(150)
            _uiState.update { it.copy(isSaving = true, saveErrorMessage = "") }
            val requestedStatus = when {
                draft.publish -> "ACTIVE"
                currentStatus.equals("Active", ignoreCase = true) -> "ACTIVE"
                else -> "DRAFT"
            }
            QuickAnnouncementRepository.update(id, QuickAnnouncementUpdateRequest(draft.message.trim(), dateTime, requestedStatus)).fold(
                { _uiState.update { it.copy(isSaving = false, saveGeneration = it.saveGeneration + 1) }; loadPage() },
                { error -> _uiState.update { it.copy(isSaving = false, saveErrorMessage = error.message.orEmpty().ifBlank { "Unable to update announcement." }) } }
            )
        }
    }

    private fun duplicate(id: String) {
        val normalizedId = id.trim()
        if (normalizedId.isBlank()) {
            _uiState.update { it.copy(actionErrorMessage = "The announcement could not be duplicated.") }
            return
        }
        mutationJob?.cancel()
        mutationJob = scope.launch {
            _uiState.update { it.copy(isMutating = true, actionErrorMessage = "") }
            QuickAnnouncementRepository.duplicate(normalizedId).fold(
                onSuccess = {
                    _uiState.update { it.copy(isMutating = false, actionErrorMessage = "") }
                    AppToastCenter.showSuccess("Announcement duplicated successfully.")
                    loadPage()
                },
                onFailure = { error ->
                    _uiState.update {
                        it.copy(
                            isMutating = false,
                            actionErrorMessage = error.message.orEmpty().ifBlank { "Unable to duplicate announcement." }
                        )
                    }
                }
            )
        }
    }

    private fun endNow(id: String) {
        val normalizedId = id.trim()
        if (normalizedId.isBlank()) {
            _uiState.update { it.copy(actionErrorMessage = "The announcement could not be ended.") }
            return
        }
        mutationJob?.cancel()
        mutationJob = scope.launch {
            _uiState.update { it.copy(isMutating = true, actionErrorMessage = "") }
            QuickAnnouncementRepository.endNow(normalizedId).fold(
                onSuccess = {
                    _uiState.update { it.copy(isMutating = false, actionErrorMessage = "") }
                    AppToastCenter.showSuccess("Announcement ended successfully.")
                    loadPage()
                },
                onFailure = { error ->
                    _uiState.update {
                        it.copy(
                            isMutating = false,
                            actionErrorMessage = error.message.orEmpty().ifBlank { "Unable to end announcement." }
                        )
                    }
                }
            )
        }
    }

    private fun delete(id: String) {
        val normalizedId = id.trim()
        if (normalizedId.isBlank()) {
            _uiState.update { it.copy(actionErrorMessage = "The announcement could not be deleted.") }
            return
        }
        mutationJob?.cancel()
        mutationJob = scope.launch {
            _uiState.update { it.copy(isMutating = true, actionErrorMessage = "") }
            QuickAnnouncementRepository.delete(normalizedId).fold(
                onSuccess = {
                    _uiState.update { it.copy(isMutating = false, actionErrorMessage = "") }
                    AppToastCenter.showSuccess("Announcement deleted successfully.")
                    loadPage()
                },
                onFailure = { error ->
                    _uiState.update {
                        it.copy(
                            isMutating = false,
                            actionErrorMessage = error.message.orEmpty().ifBlank { "Unable to delete announcement." }
                        )
                    }
                }
            )
        }
    }

    private fun QuickAnnouncementDraft.toApiDateTime(): String? {
        val parts = displayUntil.trim().split(" ").filter(String::isNotBlank)
        if (parts.size != 3) return null
        val month = mapOf("JAN" to "01","FEB" to "02","MAR" to "03","APR" to "04","MAY" to "05","JUN" to "06","JUL" to "07","AUG" to "08","SEP" to "09","OCT" to "10","NOV" to "11","DEC" to "12")[parts[1].uppercase()] ?: return null
        val day = parts[0].toIntOrNull()?.takeIf { it in 1..31 } ?: return null
        val year = parts[2].toIntOrNull()?.takeIf { it in 1900..2200 } ?: return null
        val time = normalizeAnnouncementTime(displayTime) ?: return null
        // The backend interprets timezone-less datetimes in the configured
        // school timezone (Asia/Kolkata). Do not mark the selected local time
        // as UTC, otherwise the response shifts by +05:30 on display.
        return "${year.toString().padStart(4, '0')}-$month-${day.toString().padStart(2, '0')}T${time}:00"
    }

    private fun String.isBeforeToday(): Boolean =
        appDateInputComparable(this)?.let { value ->
            appDateInputComparable(currentAppDateLabel())?.let { today -> value < today }
        } == true

    fun clear() { loadJob?.cancel(); saveJob?.cancel(); mutationJob?.cancel(); scope.cancel() }
}

private fun normalizeAnnouncementTime(value: String): String? {
    val normalized = value.trim().uppercase()
    Regex("^([01]\\d|2[0-3]):([0-5]\\d)$").matchEntire(normalized)?.let { match ->
        return "${match.groupValues[1]}:${match.groupValues[2]}"
    }

    val match = Regex("^(\\d{1,2}):([0-5]\\d)\\s*(AM|PM)$").matchEntire(normalized) ?: return null
    val hour12 = match.groupValues[1].toIntOrNull() ?: return null
    if (hour12 !in 1..12) return null
    val minute = match.groupValues[2]
    val hour24 = when {
        match.groupValues[3] == "AM" && hour12 == 12 -> 0
        match.groupValues[3] == "PM" && hour12 != 12 -> hour12 + 12
        else -> hour12
    }
    return "${hour24.toString().padStart(2, '0')}:$minute"
}

private fun String.toApiStatus(): String? = trim().takeIf { it.isNotBlank() }?.uppercase()
private fun String.toApiPeriod(): String? = when (trim()) { "Today" -> "TODAY"; "Last 7 Days" -> "LAST_7_DAYS"; "This Month" -> "LAST_30_DAYS"; else -> null }
private fun Map<String, Int>.counter(name: String): Int? = entries.firstOrNull { it.key.equals(name, true) }?.value
private fun QuickAnnouncementResponse.toUi() = QuickAnnouncementRow(
    id = qaId.orEmpty().ifBlank { id.orEmpty() },
    backendId = id.orEmpty(),
    message = message.orEmpty(),
    displayFrom = createdAt.toDisplayDateTime(),
    displayUntil = displayUntil.toDisplayDateTime(),
    status = status.toDisplayStatus(),
    createdBy = createdByName.orEmpty().ifBlank { "—" },
    createdById = createdById,
    createdAt = createdAt.toDisplayDateTime(),
    updatedBy = updatedByName.orEmpty().ifBlank { "—" },
    updatedAt = updatedAt.toDisplayDateTime()
)
private fun String?.toDisplayStatus() = when (this?.uppercase()) { "ACTIVE" -> "Active"; "EXPIRED" -> "Expired"; else -> "Draft" }
private fun String?.toDisplayDateTime(): String { val raw = this.orEmpty(); val date = raw.substringBefore('T').split('-'); if (date.size != 3) return "—"; val month = listOf("","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec")[date[1].toIntOrNull()?.coerceIn(1,12) ?: 1]; val time = raw.substringAfter('T', "").substringBefore('Z').take(5); return "${date[2]} $month ${date[0]}${if (time.isBlank()) "" else ", $time"}" }
